import {
  AlertTriangle,
  ArrowDownToLine,
  ArrowUpFromLine,
  BedDouble,
  BookOpen,
  Boxes,
  Calendar,
  Check,
  CircleDollarSign,
  Clock3,
  CreditCard,
  Download,
  Filter,
  Landmark,
  Play,
  Plus,
  Radio,
  Receipt,
  RefreshCw,
  ShieldCheck,
  ShoppingCart,
  SlidersHorizontal,
  Sparkles,
  TrendingUp,
  Users,
  Wifi,
} from "lucide-react";
import type { DetailedHelpMap, HelpContext, HelpScreenFacts } from "./types";
import { estadoDaDataDeNegocio } from "./helpFacts";

/** "há 2 dias", "hoje às 14h" — mais legível que um carimbo ISO cru. */
function quandoFoi(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return null;

  const minutos = Math.round((Date.now() - data.getTime()) / 60000);
  if (minutos < 1) return "agora há pouco";
  if (minutos < 60) return `há ${minutos} min`;
  if (minutos < 60 * 24) return `há ${Math.round(minutos / 60)} h`;
  return `em ${data.toLocaleDateString("pt-BR")}`;
}

/** Frase única sobre o pareamento das categorias com o Beds24. */
function estadoDoMapeamento(screen: HelpScreenFacts): string | null {
  const { channel_room_types_total: total, channel_room_types_mapped: mapeadas } = screen;
  if (total === undefined || mapeadas === undefined) return null;
  if (total === 0) return "nenhuma categoria de quarto cadastrada ainda — cadastre em Configurações antes de distribuir.";
  if (mapeadas === 0) return `nenhuma das ${total} categorias está vinculada ao Beds24 — enquanto isso, nada é distribuído.`;
  if (mapeadas < total) return `${mapeadas} de ${total} categorias vinculadas — as ${total - mapeadas} restantes não estão sendo distribuídas.`;
  return `todas as ${total} categorias estão vinculadas.`;
}

/** Rótulo em português das chaves de `payments.accepted_methods`. */
const rotuloDaFormaDePagamento: Record<string, string> = {
  pix: "PIX",
  credit: "cartão de crédito",
  debit: "cartão de débito",
  cash: "dinheiro",
  transfer: "transferência bancária",
};

/**
 * Uma frase sobre a subconta Asaas desta propriedade. Serve todas as telas
 * financeiras: sem subconta não existe saldo real pra mostrar, e em
 * homologação o que aparece na tela é dinheiro de teste — dizer isso evita
 * o hoteleiro fechar o caixa em cima de um número que não é dele.
 */
function estadoDaContaAsaas(context: HelpContext): string {
  if (!context.payments.asaas_configured) {
    return "a subconta Asaas desta propriedade ainda não está configurada — enquanto isso não há saldo, recebimento nem saque reais para exibir.";
  }
  return context.payments.asaas_environment === "production"
    ? "subconta Asaas configurada em produção — os valores desta tela são dinheiro real."
    : "subconta Asaas configurada em homologação (sandbox) — os valores desta tela são de teste e nenhum dinheiro real é movimentado.";
}

/** Quais formas de pagamento estão liberadas pra cobrar hóspede hoje. */
function formasHabilitadasParaHospede(context: HelpContext): string | null {
  const ativas = Object.entries(context.payments.accepted_methods)
    .filter(([, habilitada]) => habilitada)
    .map(([chave]) => rotuloDaFormaDePagamento[chave] ?? chave);

  if (ativas.length === 0) {
    return "nenhuma forma de pagamento está habilitada para cobrar hóspedes em Configurações.";
  }
  return `para cobrar hóspede, o que está habilitado em Configurações é: ${ativas.join(", ")}. A lista deste formulário é separada e não muda junto.`;
}

/**
 * Ajuda detalhada: Gerenciador de Canais, Tarifas, Estoque e Consumo,
 * Relatórios, Financeiro (+ 4 sub-rotas), Auditoria Noturna. Conteúdo
 * verificado lendo cada page component/hook real antes de escrever.
 */
export const GESTAO_COMERCIAL_HELP: DetailedHelpMap = {
  "/gerenciador-de-canais#overview": [
    {
      title: "O que esta tela faz",
      description:
        "É a ponte entre o Innsight e os sites de venda (Booking, Airbnb e afins). Em vez de você atualizar preço e disponibilidade em cada site, atualiza aqui e o Beds24 distribui. A tela tem 4 abas — esta é a Visão geral, que mostra o estado da conexão.",
      items: [
        {
          label: "Passo 1 — Ativar o motor de distribuição",
          domKey: "canais.ativar",
          visual: { icon: Sparkles, text: "Ativar motor de distribuição", tone: "primary" },
          explanation:
            "Cria a sua propriedade no Beds24. Você não precisa ter conta nem digitar senha: o Innsight faz isso por você. Este botão some sozinho depois que a ativação dá certo — se você não o está vendo, é porque esta etapa já foi concluída.",
          fact: (ctx) =>
            ctx.screen.channel_configured === undefined
              ? null
              : ctx.screen.channel_configured
                ? "já ativado — por isso o botão não aparece mais."
                : "ainda não ativado. Enquanto isso, nenhum site de venda recebe seus quartos.",
        },
        {
          label: "Passo 2 — Vincular suas categorias de quarto",
          explanation:
            "Ativar sozinho não distribui nada. Cada categoria de quarto (Standard, Luxo...) precisa ser vinculada à categoria correspondente no Beds24. Isso é feito na aba \"Inventário e tarifas\".",
          fact: (ctx) => estadoDoMapeamento(ctx.screen),
        },
        {
          label: "Testar conexão",
          domKey: "canais.testar",
          visual: { icon: ShieldCheck, text: "Testar conexão" },
          explanation:
            "Só confere se a ligação com o Beds24 está de pé e responde quantas categorias ele enxerga. Não envia preço nem disponibilidade — é seguro clicar a qualquer momento.",
        },
        {
          label: "Sincronizar tudo",
          domKey: "canais.sincronizar",
          visual: { icon: Play, text: "Sincronizar tudo" },
          explanation:
            "Empurra agora a disponibilidade e as tarifas para os sites de venda, sem esperar a rotina automática da madrugada. Use depois de mexer em preço e querer que apareça já.",
          fact: (ctx) => {
            const quando = quandoFoi(ctx.screen.channel_last_sync_at);
            return quando ? `última sincronização registrada ${quando}.` : "nenhuma sincronização registrada até agora.";
          },
        },
        {
          label: "Sinal de problema",
          explanation:
            "Sincronizações que falharam ficam registradas na aba \"Histórico de sincronização\", onde dá pra reprocessar uma a uma. Falha acumulada normalmente significa preço ou disponibilidade desatualizados nos sites de venda.",
          fact: (ctx) => {
            const falhas = ctx.screen.channel_open_failures;
            if (falhas === undefined) return null;
            return falhas === 0
              ? "nenhuma falha em aberto."
              : `${falhas} sincronização(ões) com falha — vale abrir a aba de histórico e reprocessar.`;
          },
        },
      ],
    },
    {
      title: "As 4 abas desta tela",
      items: [
        { label: "Visão geral", domKey: "canais.aba.overview", visual: { icon: Radio, text: "Visão geral" }, explanation: "Onde você está: estado da conexão e dos vínculos." },
        { label: "Inventário e tarifas", domKey: "canais.aba.inventory", visual: { icon: Boxes, text: "Inventário e tarifas" }, explanation: "Onde se vincula cada categoria de quarto ao Beds24 e se criam os quartos físicos." },
        { label: "Canais conectados", domKey: "canais.aba.channels", visual: { icon: Wifi, text: "Canais conectados" }, explanation: "Quais sites de venda (Booking, Airbnb...) estão ligados à sua propriedade." },
        { label: "Histórico de sincronização", domKey: "canais.aba.logs", visual: { icon: BookOpen, text: "Histórico de sincronização" }, explanation: "Registro de cada envio ao Beds24, com o que deu certo e o que falhou." },
      ],
    },
  ],
  "/gerenciador-de-canais#inventory": [
    {
      title: "Aba Inventário e tarifas",
      description:
        "É aqui que suas categorias de quarto passam a existir do lado do Beds24. Sem esse vínculo, o motor pode estar ativo e ainda assim nenhum site de venda mostra seus quartos.",
      items: [
        {
          label: "Provisionar categoria (uma ou todas)",
          explanation:
            "Leva uma categoria que existe no Innsight para o Beds24, criando lá o equivalente. É o caminho normal para quem está montando a distribuição a partir do Innsight.",
          fact: (ctx) => estadoDoMapeamento(ctx.screen),
        },
        {
          label: "Importar catálogo",
          explanation:
            "Caminho inverso: traz para o Innsight as categorias que já existiam no Beds24. Serve para quem já usava o Beds24 antes. O que já estiver pareado é reaproveitado, não duplicado.",
        },
        {
          label: "Criar quartos físicos",
          explanation:
            "Categoria é o tipo (\"Luxo\"); quarto físico é a unidade (101, 102...). Depois de vincular a categoria, é aqui que você cadastra de uma vez os números dos quartos que pertencem a ela.",
        },
      ],
    },
  ],
  "/gerenciador-de-canais#channels": [
    {
      title: "Aba Canais conectados",
      description:
        "Lista os sites de venda ligados à sua propriedade no Beds24 (Booking, Airbnb, Expedia...). A conexão de cada canal é feita do lado do Beds24; aqui você acompanha o que já está ligado e o estado de cada um.",
      items: [
        {
          label: "Por que um canal pode não aparecer",
          explanation:
            "A lista só é buscada quando o motor de distribuição está ativo. Se estiver vazia mesmo com o motor ativo, normalmente é porque nenhum canal foi conectado ainda no Beds24.",
          fact: (ctx) =>
            ctx.screen.channel_configured === false
              ? "o motor de distribuição ainda não está ativo — por isso nenhum canal é listado."
              : null,
        },
      ],
    },
  ],
  "/gerenciador-de-canais#logs": [
    {
      title: "Aba Histórico de sincronização",
      description:
        "Cada linha é uma conversa entre o Innsight e o Beds24: envio de tarifa, de disponibilidade, chegada de reserva. É aqui que se descobre por que um preço não apareceu num site.",
      items: [
        {
          label: "Filtro por situação",
          explanation:
            "Sucesso saiu como esperado; Falha não saiu e provavelmente deixou preço/disponibilidade desatualizados lá fora; Aguardando ainda está na fila; Aviso saiu, mas com alguma ressalva.",
          fact: (ctx) => {
            const falhas = ctx.screen.channel_open_failures;
            if (falhas === undefined) return null;
            return falhas === 0 ? "nenhuma falha em aberto." : `${falhas} registro(s) com falha aguardando reprocessamento.`;
          },
        },
        {
          label: "Reprocessar uma linha com falha",
          explanation:
            "Tenta de novo só aquele envio, sem mexer no resto. Prefira isso a \"Sincronizar tudo\" quando foi uma falha pontual.",
        },
      ],
    },
  ],
  "/tarifas": [
    {
      title: "Tarifas Dinâmicas",
      description: "Regras automáticas de ajuste de preço por ocupação ou feriado, calculadas no servidor e aplicadas ao motor de reservas e ao Beds24. Módulo depende de ativação no plano da propriedade — se não estiver ativo, a tela mostra um aviso em vez das regras.",
      items: [
        { label: "Tipo de gatilho: Ocupação", explanation: "Dispara quando a ocupação do período atinge o percentual mínimo definido no campo \"Gatilho de ocupação\"." },
        { label: "Tipo de gatilho: Feriado", explanation: "Dispara em datas do calendário nacional de feriados, sem precisar configurar um gatilho de percentual." },
        { label: "Ajuste tarifário (%)", explanation: "Percentual somado (positivo) ou subtraído (negativo) da tarifa base quando a regra dispara." },
        { label: "Prioridade", explanation: "Quando mais de uma regra se aplica ao mesmo dia, a de maior prioridade é a que roda primeiro." },
        {
          label: "Regra ativa x cadastrada",
          explanation:
            "Uma regra desativada continua salva mas não mexe em preço nenhum. Serve pra guardar uma regra sazonal sem apagar e ter que recriar depois.",
          fact: (ctx) => {
            const { yield_rules_total: total, yield_rules_active: ativas } = ctx.screen;
            if (total === undefined || ativas === undefined) return null;
            if (total === 0) return "nenhuma regra cadastrada — os preços seguem exatamente a tarifa base.";
            if (ativas === 0) return `${total} regra(s) cadastrada(s), nenhuma ativa — os preços seguem a tarifa base.`;
            return ativas === total
              ? `${ativas} regra(s) cadastrada(s) e todas ativas.`
              : `${ativas} de ${total} regra(s) ativa(s).`;
          },
        },
        { label: 'Botão "Sincronizar 60 dias"', explanation: "Empurra manualmente as tarifas calculadas dos próximos 60 dias para o Beds24. Além deste botão manual, existe também um job noturno automático que já reenvia as tarifas todos os dias — o botão serve para forçar uma atualização imediata sem esperar o ciclo automático." },
      ],
    },
  ],
  "/estoque-e-consumo": [
    {
      title: "O que esta tela faz",
      description:
        "Controla o que o hotel tem em prateleira — alimentos, bebidas, amenidades, enxoval, material de limpeza — pra você descobrir que acabou antes do hóspede descobrir. São duas abas: Itens (o estoque em si) e Ordens de Compra (as reposições pedidas ao fornecedor).",
      items: [
        {
          label: "Aba Itens",
          domKey: "estoque.aba.items",
          visual: { icon: Boxes, text: "Itens" },
          explanation:
            "A lista do que existe hoje, com estoque atual, mínimo, custo e fornecedor. Mostra apenas itens ativos: um item desativado no cadastro some da lista mas não é apagado.",
        },
        {
          label: "Aba Ordens de Compra",
          domKey: "estoque.aba.orders",
          visual: { icon: ShoppingCart, text: "Ordens de Compra" },
          explanation:
            "As reposições pedidas ao fornecedor e em que pé cada uma está. Ordem de compra não mexe no estoque sozinha — o estoque só sobe quando você registra a Entrada do que chegou.",
        },
        {
          label: "Novo Item",
          domKey: "estoque.novo_item",
          visual: { icon: Plus, text: "Novo Item", tone: "primary" },
          explanation:
            "Cadastra um item com nome, SKU, fornecedor, unidade de medida, estoque atual/mínimo e custo unitário. O estoque mínimo é o que liga o alerta de estoque baixo — deixar zero significa nunca ser avisado.",
        },
        {
          label: 'Cartão "Estoque baixo" e o selo na linha do item',
          domKey: "estoque.baixo",
          visual: { icon: AlertTriangle, text: "Estoque baixo" },
          explanation:
            "Aparece quando o estoque atual do item fica igual ou menor que o estoque mínimo cadastrado para ele. É só um alerta visual — não bloqueia consumo nem gera compra sozinho.",
          fact: (ctx) => {
            const { inventory_items_total: total, inventory_low_stock: baixo } = ctx.screen;
            if (total === undefined || baixo === undefined) return null;
            if (total === 0) return "nenhum item cadastrado ainda.";
            return baixo === 0
              ? `${total} item(ns) cadastrado(s), nenhum abaixo do mínimo.`
              : `${baixo} de ${total} item(ns) no mínimo ou abaixo dele.`;
          },
        },
        {
          label: 'Cartão "Ordens pendentes"',
          domKey: "estoque.ordens_pendentes",
          visual: { icon: ShoppingCart, text: "Ordens pendentes" },
          explanation:
            "Não confie neste número por enquanto: hoje ele repete a contagem total de itens cadastrados em vez de contar ordens de compra em aberto. Para saber quantas reposições estão pendentes de verdade, abra a aba Ordens de Compra.",
        },
        {
          label: "Registrar movimento (ícone de controles deslizantes na linha do item)",
          domKey: "estoque.movimento",
          visual: { icon: SlidersHorizontal, text: "Registrar movimento" },
          explanation:
            'Abre um formulário com 3 tipos: "Entrada" (soma ao estoque), "Saída" (subtrai do estoque) e "Ajuste" (define o novo total absoluto do estoque, não soma nem subtrai — usado para corrigir uma contagem física).',
        },
      ],
    },
  ],
  "/relatorios": [
    {
      title: "O que esta tela faz",
      description:
        "Três recortes do movimento da pousada num intervalo de datas que você escolhe: Receita, Ocupação e Hóspedes. É material de conferência e fechamento — cada número tem uma regra de cálculo própria, e vale conhecê-la antes de levar o valor para a contabilidade.",
      items: [
        {
          label: "Relatório de Receita",
          domKey: "rel.aba.revenue",
          visual: { icon: TrendingUp, text: "Receita" },
          explanation:
            "Soma o valor total das reservas cuja DATA DE CHEGADA cai no intervalo — é receita prevista pela reserva, não dinheiro recebido. Uma reserva paga antecipadamente entra no mês da hospedagem, não no mês do pagamento. O ticket médio é essa soma dividida pelo número de reservas (por reserva, não por diária). Para dinheiro que de fato entrou, use Financeiro > Entradas e saídas.",
          fact: (ctx) => {
            const statuses = ctx.enums.booking_statuses ?? [];
            const problematicos = ["CANCELLED", "NO_SHOW"].filter((status) => statuses.includes(status));
            if (problematicos.length === 0) return null;
            return `reservas com situação ${problematicos.join(" e ")} existem neste sistema e também entram nesta soma — confira antes de usar o número como faturamento.`;
          },
        },
        {
          label: "Relatório de Ocupação",
          domKey: "rel.aba.occupancy",
          visual: { icon: BedDouble, text: "Ocupação" },
          explanation:
            'Cuidado com a leitura: "Quartos ocupados" conta quantos quartos DIFERENTES tiveram alguma reserva em algum dia do intervalo — não é a média diária. Num intervalo longo quase todo quarto é usado ao menos uma vez, então a taxa tende a 100% e perde o sentido. Para ocupação real do dia, use a Visão geral. Chegadas e saídas contam reservas por data de entrada e de saída no intervalo.',
        },
        {
          label: "Relatório de Hóspedes",
          domKey: "rel.aba.guests",
          visual: { icon: Users, text: "Hóspedes" },
          explanation:
            '"Recorrentes" são os hóspedes do intervalo que já se hospedaram mais de uma vez na vida — o histórico inteiro dele, não só este período; "Novos" é simplesmente o resto. "VIPs" conta quem tem um nível de VIP diferente de Standard no cadastro do hóspede. "Principais países" mostra os cinco maiores; hóspede sem país preenchido é contado como BR.',
        },
        {
          label: "Período De / Até",
          domKey: "rel.periodo",
          visual: { icon: Calendar, text: "De … Até …" },
          explanation:
            "Define o intervalo dos três relatórios. Se a data final for anterior à inicial, a tela trava com o aviso de período inválido e não consulta nada. O intervalo também tem teto de 366 dias: acima disso o servidor recusa a consulta — para comparar dois anos, gere um relatório por ano.",
        },
        {
          label: "Atualizar",
          domKey: "rel.atualizar",
          visual: { icon: RefreshCw, text: "Atualizar" },
          explanation:
            "Refaz a consulta no servidor sem mudar o período. Útil depois de corrigir uma reserva ou rodar a auditoria noturna e querer ver o número já corrigido.",
        },
        {
          label: "Exportar PDF",
          domKey: "rel.exportar",
          visual: { icon: Download, text: "Exportar PDF", tone: "primary" },
          explanation:
            "Gera o PDF apenas do relatório que está aberto, com o mesmo período da tela. O arquivo leva só os indicadores numéricos — a lista de principais países do relatório de Hóspedes não sai no PDF. O botão fica desabilitado enquanto o relatório carrega, quando deu erro e quando o período é inválido.",
        },
      ],
    },
  ],
  "/financeiro": [
    {
      title: "O que esta tela faz",
      description:
        'É a tesouraria administrativa do hotel: o que você deve a fornecedores, agências e empresas, e as receitas que não vêm de hospedagem. Diária e consumo de hóspede NÃO entram aqui — ficam na conta da própria reserva, em Reservas > aba "Conta e pagamentos". Procurar o pagamento de um hóspede nesta tela é o engano mais comum.',
      items: [
        {
          label: "Onde está o dinheiro das reservas",
          explanation:
            "Nesta tela nada é conciliado automaticamente: tudo que aparece aqui foi digitado por alguém. O dinheiro que os hóspedes pagam entra pela Conta Asaas e aparece nas abas Conta Asaas e Entradas e saídas.",
          fact: estadoDaContaAsaas,
        },
        {
          label: "Novo lançamento",
          domKey: "fin.novo_lancamento",
          visual: { icon: Plus, text: "Novo lançamento", tone: "primary" },
          explanation:
            'Cria uma "Conta a pagar" (obrigação com fornecedor, agência ou empresa) ou uma "Outra receita gerencial" (receita administrativa que não pertence a nenhuma reserva). Nos dois casos é só uma anotação de compromisso: nada de dinheiro se move até você registrar a baixa. Competência é o mês a que o valor pertence, Emissão é a data do documento e Vencimento é o que manda nos indicadores e no alerta de vencido.',
        },
        {
          label: "Registrar baixa / Registrar entrada",
          domKey: "fin.liquidar",
          visual: { icon: Check, text: "Registrar baixa" },
          explanation:
            "Confirma que o dinheiro realmente saiu ou entrou fora do sistema. Registre um valor menor que o saldo e o lançamento fica como Parcial, com o restante ainda em aberto; valor acima do saldo é recusado. Confira antes de salvar: a tela não tem como desfazer nem estornar uma baixa, e um lançamento já liquidado ou cancelado também não pode mais ser editado.",
          fact: formasHabilitadasParaHospede,
        },
        {
          label: "Seletor de período no topo",
          domKey: "fin.periodo",
          explanation:
            "Atenção: hoje esta caixa ainda não muda o recorte dos números. Escolhendo Hoje, Semana ou Ano, os indicadores e a lista continuam mostrando o mês corrente. Considere sempre que está olhando o mês atual, e confira lançamento a lançamento quando precisar de outro intervalo.",
        },
      ],
    },
    {
      title: "Os cinco indicadores do topo",
      description:
        "Os quatro primeiros olham para o vencimento ou para a data da baixa dentro do mês corrente. Nenhum deles inclui reservas.",
      items: [
        {
          label: "A pagar em aberto",
          domKey: "fin.kpi.payable_open",
          visual: { icon: ArrowDownToLine, text: "A pagar em aberto" },
          explanation:
            "Quanto ainda falta pagar (valor do lançamento menos o que já foi baixado) somando as contas a pagar que vencem no mês. Um lançamento cancelado com saldo em aberto continua entrando nesta conta — se o número parecer alto demais, procure cancelados na lista abaixo.",
        },
        {
          label: "Outras receitas previstas",
          domKey: "fin.kpi.receivable_open",
          visual: { icon: ArrowUpFromLine, text: "Outras receitas previstas" },
          explanation:
            "O mesmo cálculo do lado das receitas gerenciais: quanto você ainda espera receber de algo que não é hospedagem. É previsão digitada, não dinheiro em conta.",
        },
        {
          label: "Pago no período",
          domKey: "fin.kpi.paid",
          visual: { icon: Check, text: "Pago no período" },
          explanation:
            "Soma das baixas de contas a pagar registradas no mês, pela data da baixa. Pagar hoje um boleto que venceu no mês passado entra aqui, mesmo que o lançamento não apareça em A pagar em aberto.",
        },
        {
          label: "Entradas manuais no período",
          domKey: "fin.kpi.received",
          visual: { icon: CircleDollarSign, text: "Entradas manuais no período" },
          explanation:
            'Soma das entradas de receitas gerenciais registradas no mês. Chama-se "manuais" porque alguém digitou: recebimento de reserva nunca aparece neste número.',
        },
        {
          label: "Vencidos",
          domKey: "fin.kpi.overdue",
          explanation:
            "É uma QUANTIDADE de lançamentos, não um valor em dinheiro: quantos venceram, ainda não foram liquidados nem cancelados. Calculado na hora, comparando o vencimento com a data de hoje — um lançamento passa a contar sozinho no dia seguinte ao vencimento, sem ninguém mexer nele.",
        },
      ],
    },
    {
      title: "A lista de lançamentos",
      items: [
        {
          label: "Filtro Todos / A pagar / Outras receitas",
          domKey: "fin.filtro_direcao",
          visual: { icon: Filter, text: "Todos · A pagar · Outras receitas" },
          explanation:
            "Filtra apenas a lista abaixo. Os cinco indicadores do topo continuam considerando tudo — eles não acompanham este filtro.",
        },
        {
          label: "Lançamentos corporativos",
          domKey: "fin.lancamentos",
          visual: { icon: Receipt, text: "Lançamentos corporativos" },
          explanation:
            "Cada linha traz a contraparte, o vencimento, a categoria, o valor total e, logo abaixo, a situação com o saldo que ainda falta. Seta vermelha para baixo é conta a pagar; seta verde para cima é receita gerencial. Quando o saldo chega a zero o botão de baixa desaparece da linha.",
        },
      ],
    },
  ],
  "/financeiro/conta-asaas": [
    {
      title: "O que esta tela faz",
      description:
        "É a conta de pagamentos da propriedade no Asaas: onde o dinheiro dos hóspedes cai, quando ele fica liberado e por onde você o transfere para o banco do hotel. Nada aqui tem relação com a mensalidade que a pousada paga ao InnSight — isso fica em Histórico de Cobrança.",
      items: [
        {
          label: "Saldo disponível para saque",
          domKey: "fin.saldo",
          explanation:
            "Só o que já foi liquidado E já foi liberado pelo Asaas. Cobrança confirmada mas ainda em prazo de liberação (parcela de cartão, por exemplo) não está neste número — está no cartão ao lado.",
          fact: estadoDaContaAsaas,
        },
        {
          label: "Recebíveis futuros",
          domKey: "fin.recebiveis_futuros",
          explanation:
            "Dinheiro já confirmado pelo Asaas, mas ainda dentro do prazo de crédito. É o que vai virar saldo disponível nos próximos dias sem você fazer nada — ou hoje, se você optar por antecipar na aba Antecipações.",
        },
        {
          label: "Ambiente",
          domKey: "fin.ambiente",
          explanation:
            'Diz se a conta está em Produção (dinheiro real) ou Sandbox (homologação). Em Sandbox tudo funciona igual na tela, mas nenhum valor sai ou entra de verdade — não use esses números para fechar caixa.',
          fact: estadoDaContaAsaas,
        },
        {
          label: "Limite operacional informado pelo Asaas",
          domKey: "fin.limite_operacional",
          explanation:
            "Este aviso só aparece quando o Asaas informa um teto de cobranças para a conta. Se ficar vermelho, o limite foi atingido e novas cobranças podem ser recusadas até o Asaas liberar — é assunto para tratar com o Asaas, não se resolve por aqui.",
        },
        {
          label: "Conta de saque da propriedade",
          domKey: "fin.conta_saque",
          visual: { icon: Landmark, text: "Conta de saque da propriedade" },
          explanation:
            "Para onde o dinheiro vai quando você saca. Só aceita conta PJ da mesma empresa da propriedade — titular e CNPJ vêm do cadastro e não são digitados. Escolhendo PIX pelo CNPJ não há formulário nenhum: o saque usa a chave CNPJ já validada. Existe um único destino ativo: salvar uma conta TED substitui o PIX, e vice-versa.",
        },
        {
          label: "Agenda de recebimentos",
          domKey: "fin.agenda",
          visual: { icon: Clock3, text: "Agenda de recebimentos" },
          explanation:
            "Lista apenas o que ainda NÃO está disponível: cobranças confirmadas aguardando crédito e cobranças aguardando o pagamento do hóspede. O que já caiu no saldo sai desta lista. A data mostrada em cada cartão vale mais que a regra geral do rodapé.",
        },
        {
          label: "Solicitar saque",
          domKey: "fin.saque",
          visual: { text: "Revisar saque", tone: "primary" },
          explanation:
            'O botão chama "Revisar saque" de propósito: ele ainda não transfere nada, só abre uma conferência com o destino, a forma e o saldo que sobra depois. O dinheiro só sai ao confirmar na segunda tela. Valor acima do saldo disponível é recusado pelo Asaas.',
          fact: estadoDaContaAsaas,
        },
        {
          label: "Transferências e saques",
          domKey: "fin.transferencias",
          visual: { icon: ArrowUpFromLine, text: "Transferências e saques" },
          explanation:
            "O andamento de cada saque pedido. Enquanto estiver Pendente dá para cancelar por aqui; depois de efetivado, não. Esta lista mostra as transferências do mês corrente.",
        },
        {
          label: "Configuração fiscal",
          domKey: "fin.fiscal",
          explanation:
            "Dados para a propriedade emitir nota fiscal de serviço pela prefeitura dela. É a nota que o hotel emite para o hóspede ou para a empresa — não tem nada a ver com a nota da mensalidade InnSight.",
        },
        {
          label: "Seletor de período no topo",
          domKey: "fin.periodo",
          explanation:
            "Não afeta esta tela. O saldo é sempre o de agora, e a agenda, o extrato e as transferências mostram sempre o mês corrente, qualquer que seja a opção escolhida.",
        },
      ],
    },
  ],
  "/financeiro/antecipacoes": [
    {
      title: "O que esta tela faz",
      description:
        "Receber hoje um dinheiro que só cairia daqui a alguns dias, pagando uma taxa por isso. Quem decide se libera é o Asaas, cobrança por cobrança — o Innsight só encaminha o pedido. Regra prática: PIX é creditado rápido e não se antecipa; cartão pode ser antecipado após análise; boleto só aparece quando o Asaas libera limite para ele.",
      items: [
        {
          label: "Filtro por forma de pagamento",
          domKey: "fin.antecipacoes.filtro",
          visual: { icon: Filter, text: "Todos · Cartão · PIX · Boleto" },
          explanation:
            'Reduz a tabela a uma forma de pagamento. O botão "Antecipar tudo" respeita o filtro: ele seleciona só o que está aptos na visão atual, não a carteira inteira.',
        },
        {
          label: "Tabela de recebíveis",
          domKey: "fin.antecipacoes.tabela",
          explanation:
            '"Apta para análise" quer dizer apenas que a cobrança pode ser enviada ao Asaas — não é aprovação nem promessa de crédito. A coluna Crédito é a data em que o dinheiro cairia sem antecipação. Depois do envio, a situação de cada linha muda sozinha (Em análise, Aprovada, Crédito agendado, Recusada) enquanto a tela estiver aberta.',
          fact: estadoDaContaAsaas,
        },
        {
          label: "Solicitar (uma cobrança)",
          domKey: "fin.antecipacoes.solicitar",
          visual: { text: "Solicitar" },
          explanation:
            "Em produção abre a simulação oficial do Asaas, com o custo real daquela cobrança antes de você confirmar. Em homologação (sandbox) não existe simulação prévia: o pedido cai direto na tela de revisão, onde a taxa do Asaas aparece apenas como \"calculada por cobrança\".",
          fact: (ctx) =>
            ctx.payments.asaas_environment === "sandbox"
              ? "esta propriedade está em homologação (sandbox) — não haverá simulação de custo antes de confirmar."
              : null,
        },
        {
          label: "Antecipar tudo (N)",
          domKey: "fin.antecipacoes.lote",
          visual: { text: "Antecipar tudo", tone: "primary" },
          explanation:
            "Envia de uma vez todas as cobranças aptas do filtro atual. Passa por uma revisão antes, mas ali só aparece o líquido atual e a taxa Innsight — a taxa de antecipação do Asaas é calculada cobrança a cobrança e só se conhece no resultado. O Asaas pode aprovar umas e recusar outras no mesmo lote.",
        },
        {
          label: "Antecipar selecionados (N)",
          domKey: "fin.antecipacoes.selecionados",
          visual: { text: "Antecipar selecionados", tone: "primary" },
          explanation:
            "O mesmo envio em lote, só que restrito às linhas que você marcou na caixinha. Cobranças que não estão aptas não podem ser marcadas.",
        },
        {
          label: "Como as taxas se somam",
          explanation:
            "Primeiro o Asaas desconta a taxa de antecipação dele; sobre o que sobra o Innsight aplica a taxa administrativa de 1%. As duas são mostradas separadamente antes da confirmação, e a parte do Innsight é hoje apenas uma estimativa informativa: nenhuma cobrança automática é feita ao solicitar.",
        },
        {
          label: "Histórico de antecipações",
          domKey: "fin.antecipacoes.historico",
          explanation:
            "O que já foi solicitado, com bruto, taxa do Asaas, taxa Innsight estimada, líquido estimado e situação. Os valores vêm da própria subconta Asaas — é a fonte a usar para conferir quanto a operação custou de verdade.",
        },
      ],
    },
  ],
  "/financeiro/movimentacoes": [
    {
      title: "O que esta tela faz",
      description:
        'Aqui é dinheiro que existiu: os pagamentos das reservas e cada evento que mexeu no saldo da subconta Asaas. É o contrário do "Financeiro gerencial", que é só previsão digitada. Para acompanhar ou cancelar um saque, vá para a aba Conta Asaas.',
      items: [
        {
          label: "Recebimentos das reservas",
          domKey: "fin.movimentacoes.recebimentos",
          explanation:
            "Cada linha é um pagamento registrado numa reserva: quem pagou, por qual canal, o valor bruto, a comissão da OTA e o líquido estimado do hotel. Mostra os pagamentos do mês corrente.",
          fact: estadoDaContaAsaas,
        },
        {
          label: 'O quadro "Disponível no Asaas" de cada linha',
          explanation:
            "Diz quanto daquele pagamento o Asaas confirmou como recebido na subconta. Fica zerado quando o dinheiro não passou pela subconta (dinheiro em espécie, maquininha própria, cobrança do próprio canal de venda) e também quando a cobrança é antiga demais para ser reencontrada na consulta. Zero aqui não significa hóspede inadimplente — é só ausência de confirmação do lado do Asaas.",
        },
        {
          label: "Solicitar estorno",
          domKey: "fin.movimentacoes.estorno",
          visual: { text: "Solicitar estorno", tone: "danger" },
          explanation:
            "Só aparece em cobranças que o Asaas confirmou como recebidas. Devolve o valor INTEGRAL ao hóspede — não existe estorno parcial nesta tela, e não há como desfazer depois de o Asaas aceitar. Pede confirmação antes de enviar.",
        },
        {
          label: "Extrato financeiro Asaas",
          domKey: "fin.movimentacoes.extrato",
          explanation:
            "O extrato bruto da subconta: tudo que alterou o saldo, incluindo entradas, tarifas cobradas pelo Asaas, estornos, repasses e saques já efetivados. Verde é entrada, vermelho é saída, e cada linha mostra o saldo que ficou depois dela. Cobre o mês corrente e traz no máximo os 100 eventos mais recentes — num mês muito movimentado, os mais antigos não aparecem.",
        },
        {
          label: "Seletor de período no topo",
          domKey: "fin.periodo",
          explanation:
            "Não afeta esta tela: os recebimentos e o extrato mostram sempre o mês corrente, independentemente da opção escolhida.",
        },
      ],
    },
  ],
  "/financeiro/historico-de-cobrancas": [
    {
      title: "O que esta tela faz",
      description:
        "Esta tela é sobre a ASSINATURA da pousada na plataforma InnSight — a mensalidade que o hoteleiro paga para usar o sistema. Não tem nenhuma relação com o dinheiro das hospedagens, com a Conta Asaas da propriedade nem com o que os hóspedes pagam.",
      items: [
        {
          label: "Plano Atual",
          domKey: "cobranca.plano",
          visual: { icon: CreditCard, text: "Plano Atual" },
          explanation:
            "Qual plano InnSight está contratado e quanto custa por mês. O valor exibido é o preço de tabela do plano — descontos comerciais combinados por fora podem não estar refletidos aqui.",
        },
        {
          label: "Próximo Vencimento",
          domKey: "cobranca.vencimento",
          visual: { icon: Calendar, text: "Próximo Vencimento" },
          explanation:
            'Deveria mostrar a data da próxima mensalidade, mas hoje este cartão aparece sempre como "—": a data ainda não chega do servidor nesta tela. Não interprete o traço como assinatura sem cobrança — confirme a data com o suporte InnSight.',
        },
        {
          label: "Status",
          domKey: "cobranca.status",
          visual: { icon: Receipt, text: "Status" },
          explanation:
            "Mostra Ativo, Trial, Atrasado ou Cancelado. Situações fora dessas quatro — assinatura suspensa ou aguardando o primeiro pagamento, por exemplo — ainda são exibidas como Ativo. Se o sistema estiver bloqueando algo, não conclua pelo selo: confirme com o suporte.",
        },
        {
          label: "Pagamentos",
          domKey: "cobranca.pagamentos",
          visual: { icon: Receipt, text: "Pagamentos" },
          explanation:
            'Deveria listar as mensalidades já processadas, com data, descrição, valor e situação. Hoje a lista chega sempre vazia e a tela mostra "Nenhum registro encontrado", mesmo em contas com pagamentos feitos — o histórico de faturas ainda não é enviado para esta tela. Para segunda via ou comprovante, procure o suporte InnSight.',
        },
        {
          label: "Exportar",
          domKey: "cobranca.exportar",
          visual: { icon: Download, text: "Exportar" },
          explanation:
            'Só aparece quando existe algum pagamento na lista. Ainda não gera arquivo: por enquanto avisa "Exportação em breve".',
        },
      ],
    },
  ],
  "/auditoria-noturna": [
    {
      title: "O que esta tela faz",
      description:
        "É o fechamento do dia. Ela lança as diárias de quem está hospedado, processa quem não apareceu, calcula os indicadores do dia e avança a data de negócio. Sem rodar, o hotel fica 'parado' num dia anterior e as diárias seguintes não entram no faturamento.",
      items: [
        {
          label: "Data de negócio (o dia em que o hotel está)",
          explanation:
            "Cada execução avança um dia. Se ficaram dias para trás, é preciso rodar uma vez para cada dia até alcançar hoje — a auditoria não pula datas.",
          fact: (ctx) => estadoDaDataDeNegocio(ctx.screen),
        },
        {
          label: "O que será lançado nesta execução",
          explanation:
            "Uma diária para cada hóspede hospedado na data de negócio. Quem tem saída marcada para o dia entra no fluxo de encerramento da conta.",
          fact: (ctx) => {
            const { bookings_checked_in: dentro, bookings_departing_today: saindo } = ctx.screen;
            if (dentro === undefined) return null;
            return `${dentro} hóspede(s) no hotel agora; ${saindo ?? 0} com saída prevista para hoje.`;
          },
        },
      ],
    },
    {
      title: "Detalhes da execução",
      description: "Fechamento diário do PMS: lança as diárias do dia, processa no-shows, calcula indicadores USALI (ocupação, ADR, RevPAR, receita) e avança a data de negócio para o próximo dia.",
      items: [
        { label: "Bloqueios operacionais", explanation: "Se existir algum impedimento crítico (ex.: pendência que precisa ser resolvida antes), a auditoria não pode ser executada até ele ser corrigido — o motivo aparece num aviso só depois de tentar iniciar, não fica listado na tela de antemão." },
        { label: "Exceções operacionais", explanation: "Situações que não bloqueiam a auditoria, mas ficam registradas (\"congeladas\") no relatório de fechamento do dia." },
        { label: 'Botão de iniciar a auditoria', explanation: "Pede confirmação antes de rodar — a execução avança a data de negócio do hotel, então não pode ser desfeita com um simples cancelar depois de concluída." },
      ],
    },
  ],
};
